<header class="app-header">
  <a [routerLink]="routes.HOME" class="app-header__brand" (click)="closeMobileMenu()">
    <img src="/assets/images/logo3.png" alt="Amaalmomineen - Your Companion in Imaan" class="app-header__logo-img" />
  </a>

  <nav class="app-header__nav" [class.app-header__nav--open]="mobileMenuOpen()">
    @for (link of navLinks; track link.path) {
      <a
        class="app-header__nav-link"
        [routerLink]="link.path"
        routerLinkActive="app-header__nav-link--active"
        [routerLinkActiveOptions]="{ exact: link.path === '/' }"
        (click)="closeMobileMenu()"
      >
        {{ link.label | translate }}
      </a>
    }

    <div class="app-header__more">
      <button type="button" class="app-header__nav-link app-header__more-toggle" (click)="toggleMoreMenu()">
        {{ 'nav.more' | translate }} <span class="app-header__caret">▾</span>
      </button>
      @if (moreMenuOpen()) {
        <div class="app-header__more-menu">
          <a [routerLink]="routes.MASJIDS" (click)="closeMoreMenu()">{{ 'nav.masjids' | translate }}</a>
          <a [routerLink]="routes.NAMES_OF_ALLAH" (click)="closeMoreMenu()">{{ 'nav.99_names' | translate }}</a>
          <a [routerLink]="routes.PRAYER_TIMES" (click)="closeMoreMenu()">{{ 'nav.prayer_times' | translate }}</a>
        </div>
      }
    </div>
  </nav>

  <div class="app-header__actions">
    <!-- Language Switcher -->
    <div class="app-header__lang">
      <button
        type="button"
        class="app-header__lang-btn"
        [attr.aria-label]="'header.choose_language' | translate"
        (click)="toggleLangMenu()"
      >
        <span class="app-header__lang-flag">{{ languageService.currentLanguage().flag }}</span>
        <span class="app-header__lang-code">{{ languageService.currentLang().toUpperCase() }}</span>
        <span class="app-header__caret">▾</span>
      </button>
      @if (langMenuOpen()) {
        <div class="app-header__lang-menu">
          <span class="app-header__lang-title">{{ 'header.choose_language' | translate }}</span>
          @for (lang of languageService.languages; track lang.code) {
            <button
              type="button"
              class="app-header__lang-option"
              [class.app-header__lang-option--active]="lang.code === languageService.currentLang()"
              (click)="selectLanguage(lang.code)"
            >
              <span class="app-header__lang-option-flag">{{ lang.flag }}</span>
              <span class="app-header__lang-option-label">{{ 'languages.' + lang.code | translate }}</span>
              @if (lang.code === languageService.currentLang()) {
                <span class="app-header__lang-check">✓</span>
              }
            </button>
          }
        </div>
      }
    </div>

    <!-- Theme -->
    <div class="app-header__theme">
      <button
        type="button"
        class="app-header__icon-btn"
        [attr.aria-label]="'header.choose_theme' | translate"
        (click)="toggleThemeMenu()"
      >
        <span class="app-header__theme-swatch" [style.background]="currentThemeOption()?.swatch"></span>
      </button>
      @if (themeMenuOpen()) {
        <div class="app-header__theme-menu">
          @for (theme of themeService.themes; track theme.id) {
            <button
              type="button"
              class="app-header__theme-option"
              [class.app-header__theme-option--active]="theme.id === themeService.currentTheme()"
              (click)="selectTheme(theme.id)"
            >
              <span class="app-header__theme-swatch" [style.background]="theme.swatch"></span>
              {{ theme.label }}
            </button>
          }
        </div>
      }
    </div>

    <button type="button" class="app-header__icon-btn" aria-label="Search">🔍</button>

    <!-- Notifications -->
    <div class="app-header__notif">
      <button
        type="button"
        class="app-header__icon-btn app-header__icon-btn--bell"
        [attr.aria-label]="'header.notifications' | translate"
        (click)="toggleNotifMenu()"
      >
        🔔
        @if (notificationService.unreadCount() > 0) {
          <span class="app-header__badge">{{ notificationService.unreadCount() }}</span>
        }
      </button>
      @if (notifMenuOpen()) {
        <div class="app-header__notif-dropdown">
          <div class="app-header__notif-header">
            <span class="app-header__notif-title">{{ 'header.notifications' | translate }}</span>
            @if (notificationService.unreadCount() > 0) {
              <button type="button" class="app-header__notif-mark-all" (click)="notificationService.markAllRead()">
                {{ 'header.mark_all_read' | translate }}
              </button>
            }
          </div>
          <div class="app-header__notif-list">
            @for (notif of notificationService.notifications(); track notif.id) {
              <div class="app-header__notif-item" [class.app-header__notif-item--unread]="!notif.read" (click)="notificationService.markRead(notif.id)">
                <span class="app-header__notif-icon" [style.background]="notificationService.categoryMeta[notif.category].color">
                  {{ notificationService.categoryMeta[notif.category].icon }}
                </span>
                <div class="app-header__notif-body">
                  <span class="app-header__notif-category" [style.color]="notificationService.categoryMeta[notif.category].color">
                    {{ notificationService.categoryMeta[notif.category].label }}
                  </span>
                  <p class="app-header__notif-message">{{ notif.message }}</p>
                  <span class="app-header__notif-time" [title]="notificationService.formatDateTime(notif.time)">
                    {{ notificationService.formatTime(notif.time) }}
                  </span>
                </div>
                @if (!notif.read) {
                  <span class="app-header__notif-dot"></span>
                }
              </div>
            } @empty {
              <div class="app-header__notif-empty">{{ 'header.no_notifications' | translate }}</div>
            }
          </div>
        </div>
      }
    </div>

    <!-- User -->
    <div class="app-header__user-menu">
      <button type="button" class="app-header__user" (click)="toggleUserMenu()">
        <span class="app-header__user-avatar" aria-hidden="true">👤</span>
        <span class="app-header__user-text">
          @if (authService.isAuthenticated()) {
            <span class="app-header__user-greeting">{{ 'common.assalamu_alaikum' | translate }},</span>
            <span class="app-header__user-name">{{ authService.userName() || 'User' }}</span>
          } @else {
            <span class="app-header__user-name">{{ 'header.guest_profile' | translate }}</span>
          }
        </span>
        <span class="app-header__caret">▾</span>
      </button>
      @if (userMenuOpen()) {
        <div class="app-header__user-dropdown">
          @if (authService.isAuthenticated()) {
            <a [routerLink]="routes.DASHBOARD_SETTINGS" (click)="userMenuOpen.set(false)">
              <span aria-hidden="true">👤</span> {{ 'header.profile' | translate }}
            </a>
            <button type="button" (click)="logout()">
              <span aria-hidden="true">↪</span> {{ 'header.logout' | translate }}
            </button>
          } @else {
            <a [routerLink]="routes.AUTH.LOGIN" (click)="userMenuOpen.set(false)">
              <span aria-hidden="true">↪</span> {{ 'header.login' | translate }}
            </a>
          }
        </div>
      }
    </div>

    <button
      type="button"
      class="app-header__menu-toggle"
      aria-label="Toggle menu"
      [class.app-header__menu-toggle--open]="mobileMenuOpen()"
      (click)="toggleMobileMenu()"
    >
      <span></span>
      <span></span>
      <span></span>
    </button>
  </div>
</header>
